/**
 * Getting a walk's photos OUT of the console — as saved files, and as a drag.
 *
 * The destination is Procore in the next tab, so all three paths here exist to
 * remove a step from "the photos are on this page and they need to be over
 * there":
 *
 *  - **Save one photo**, named after its caption rather than after an R2
 *    object key.
 *  - **Save all of them** in one click, as one zip — not N downloads, which
 *    Chrome interrupts with a "download multiple files?" prompt.
 *  - **Drag** a photo (or the whole set) out of the page and drop it on
 *    another window, another app, or the desktop.
 *
 * ## Why a ticket, and why the drag needs one
 *
 * A drag out of a Chromium page is a `DownloadURL` transfer: the BROWSER
 * fetches the URL, so nothing the page would attach to a `fetch` — the Clerk
 * bearer token above all — is present. The same is true of a plain download
 * link. So the URL carries a short-lived ticket instead
 * (`server/downloadTicket.ts`), scoped to one walk's photos and nothing else.
 *
 * A ticket takes one round trip to mint, which is why it is fetched when the
 * page loads rather than when the user starts dragging: `dragstart` is
 * synchronous — there is no awaiting inside it — so a ticket that is not
 * already in hand cannot be used. Without one (the request failed, or it
 * expired while the page sat open) the drag degrades to handing over the
 * image's own URL, which still drops as a link into a document and still
 * saves to the desktop in most browsers; downloads degrade to an authenticated
 * `fetch` and a blob, which always works but holds the file in memory.
 *
 * ## What this deliberately does not promise
 *
 * Whether a given DROP TARGET accepts a dragged file is the target's decision,
 * not ours. Chromium hands a `DownloadURL` drag to the OS as a file, which is
 * what a native app and the desktop take; a web page's own dropzone receives
 * whatever that page's script asks for. So the drag is offered next to a
 * download button, never instead of one.
 */

import { authHeaders } from "../auth/token";
import { consoleUrl } from "../basePath";

/** Absolute (origin-qualified) — a `DownloadURL` payload must be absolute. */
function absolute(path: string): string {
  return new URL(consoleUrl(path), location.href).href;
}

/** One photo, as a download URL. `ticket` may be null; the URL then needs a
 *  bearer, so only `downloadFile` can use it. */
export function photoFileUrl(visitId: string, photoId: string, ticket: string | null): string {
  const query = ticket ? `?t=${encodeURIComponent(ticket)}` : "";
  return absolute(
    `/api/walks/${encodeURIComponent(visitId)}/photos/${encodeURIComponent(photoId)}${query}`,
  );
}

/** Every photo of the walk, as one zip. */
export function photosZipUrl(visitId: string, ticket: string | null): string {
  const query = ticket ? `?t=${encodeURIComponent(ticket)}` : "";
  return absolute(`/api/walks/${encodeURIComponent(visitId)}/photos.zip${query}`);
}

/**
 * Save `url` to disk as `suggestedName`.
 *
 * With a ticket in the URL the anchor is enough: the response is same-origin
 * and carries `Content-Disposition: attachment`, so the browser streams it to
 * disk without the page holding the bytes. Without one, the request needs the
 * bearer header, so it goes through `fetch` and a blob — correct, just less
 * kind to memory. A JSON `body` uses POST for a selected-photo download.
 * Returns false when the download could not be started at
 * all, so the caller can SAY so rather than leaving a button that did nothing.
 */
export async function downloadFile(
  url: string,
  suggestedName: string,
  { authenticated, body }: { authenticated: boolean; body?: string },
): Promise<boolean> {
  try {
    let href = url;
    let revoke: string | null = null;
    if (authenticated) {
      const response = await fetch(url, {
        method: body === undefined ? "GET" : "POST",
        headers: { ...(await authHeaders()), ...(body === undefined ? {} : { "content-type": "application/json" }) },
        body,
      });
      if (!response.ok) return false;
      // Same-origin file endpoints supply the actual name and media extension.
      const encodedName = response.headers.get("content-disposition")?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
      if (encodedName) suggestedName = decodeURIComponent(encodedName);
      href = URL.createObjectURL(await response.blob());
      revoke = href;
    }
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = suggestedName;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Chrome needs the object URL to outlive the click; a minute is far more
    // than the browser needs to start reading it and short enough that a long
    // session does not accumulate blobs.
    if (revoke) setTimeout(() => URL.revokeObjectURL(revoke!), 60_000);
    return true;
  } catch {
    return false;
  }
}

/**
 * Load a drag with a file.
 *
 * `DownloadURL` is the Chromium mechanism that turns a drag out of a page into
 * a FILE at the other end; `text/uri-list` and `text/plain` are the fallbacks
 * every other target understands (a browser tab, a document, an editor).
 * `copy` rather than `move` because nothing leaves the console.
 */
export function setDragFile(
  transfer: DataTransfer,
  { url, name, mimeType }: { url: string; name: string; mimeType: string },
): void {
  transfer.effectAllowed = "copy";
  transfer.dropEffect = "copy";
  // `<mime>:<filename>:<absolute url>` — the format Chromium parses.
  transfer.setData("DownloadURL", `${mimeType}:${name}:${url}`);
  transfer.setData("text/uri-list", url);
  transfer.setData("text/plain", url);
}
